// components/home/QuizLoop.tsx
// Quiz loop - STEP di quiz con fasi context/quiz/recall
"use client";

import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useUI } from '@/app/context/UIContext';
import { useRouter } from 'next/navigation';

type Phase = 'loading' | 'select_module' | 'context' | 'quiz' | 'results' | 'recall' | 'module_complete';

interface QuizItem {
  id: string;
  numero: number;
  versione_affermativa: string;
  versione_interrogativa: string;
  options_quantized: string[];
  correct_answer_letter: string;
  tags_advanced?: {
    tema: string;
    concetti: string[];
    difficolta_calcolata: string;
  };
  correlazioni?: {
    quiz_correlati: number[];
    score_connessione: number;
  };
}

interface Module {
  nome: string;
  quiz_count: number;
  step_count: number;
  status: string;
  current_step: number;
  completion_percentage: number;
  adaptive_mode: string;
  has_pending_recall: boolean;
  is_retraining: boolean;
  last_accessed_at: string | null;
  last_score_percentage: number | null;
}

interface NextActivity {
  type: string;
  title: string;
  description: string;
  quiz_count: number;
  step?: { id: string };
  recall?: { id: string; recall_type: string };
}

interface Answer {
  quiz_id: string;
  correct: boolean;
}

const VF_DATA = [
  {
    titolo: 'Il confronto prima della sanzione',
    quando: 'Un collaboratore ha performance inadeguata, un comportamento scorretto, o si lamenta con la Direzione.',
    logica: 'La PA preferisce sempre il dialogo diretto e costruttivo alla punizione immediata. Prima si capisce, poi si agisce.',
    esempio: 'Un collaboratore con performance scarsa si lamenta con la Direzione di avere poche opportunità di carriera. Non si attende la comunicazione ufficiale né si convoca per rimproverare — si convoca per capire le motivazioni, si sottolinea la scarsa performance e si suggerisce un percorso di miglioramento. Il confronto aperto viene prima di qualsiasi provvedimento.',
  },
  {
    titolo: 'Il dialogo riservato prima della segnalazione',
    quando: "Un collega o collaboratore ha un comportamento scorretto, denigratorio o irrispettoso — verso di te, verso altri, verso gli utenti.",
    logica: 'La PA affronta i comportamenti scorretti in modo graduale: prima il confronto diretto e privato, poi — se persiste — la segnalazione formale.',
    esempio: 'Un collaboratore addetto al pubblico ha risposto in malo modo agli utenti. Non si manda una mail di richiamo, non si segnala subito al capo — si convoca il dipendente, si capiscono le motivazioni e si sottolinea che il comportamento corretto è deontologicamente indispensabile. La porta alla comunicazione resta aperta.',
  },
  {
    titolo: 'Il coinvolgimento prima della decisione',
    quando: 'Devi introdurre un cambiamento — nuove procedure, nuova organizzazione, nuovi obiettivi — e incontri resistenza o inerzia.',
    logica: 'Imporre il cambiamento genera resistenza. La PA valorizza il coinvolgimento attivo delle persone nelle decisioni che le riguardano — non come debolezza, ma come metodo.',
    esempio: 'Devi riorganizzare il reparto riducendo i costi del 10% in tre anni. I dipendenti sono riluttanti. La risposta corretta non è applicare subito i nuovi processi né rimandare — è creare gruppi di lavoro che partecipino alla definizione delle proposte. Il cambiamento guidato dal basso è più solido.',
  },
  {
    titolo: 'La partecipazione prima della direttiva',
    quando: 'Il tuo gruppo non performa, la produttività cala, i ruoli non sono chiari.',
    logica: "La ridefinizione unilaterale e direttiva di ruoli e responsabilità è l'ultima opzione, non la prima. La PA preferisce processi partecipativi anche nella gestione ordinaria.",
    esempio: "Da sei mesi la produttività del gruppo non è adeguata nonostante l'impegno di tutti. Non si ridefiniscono i ruoli in modo direttivo, non si temporeggia — si consente al gruppo di partecipare alla ridefinizione e si definiscono insieme le modalità di verifica. La squadra costruisce la soluzione con te.",
  },
  {
    titolo: "L'azione diretta prima della delega verso l'alto",
    quando: "C'è un problema di clima, un collega in difficoltà, una tensione nel team che potresti ignorare o passare al superiore.",
    logica: 'La PA valorizza chi interviene direttamente e responsabilmente, senza aspettare che qualcun altro risolva. Lamentarsi con altri o non fare nulla è sempre la risposta sbagliata.',
    esempio: "Il capo spinge per la promozione di un collega bravo ma poco proattivo, e questo genera malcontento nel team. Non ci si lamenta con i colleghi, non ci si limita a segnalare al capo — si parla direttamente con il collega e lo si sprona ad avere un atteggiamento più proattivo. L'iniziativa personale è valorizzata.",
  },
  {
    titolo: 'La comprensione prima del giudizio',
    quando: "Devi gestire una persona difficile — un collega con atteggiamento chiuso, un collaboratore che rimanda un'immagine negativa, qualcuno che non si integra.",
    logica: "La PA non giudica dalla superficie. Prima di agire, capisce. Il dialogo esplorativo — capire perché una persona si comporta in un certo modo — precede qualsiasi intervento.",
    esempio: "Ti viene chiesto di supportare professionalmente una persona di un team esterno che rimanda un'immagine poco collaborativa. Non le si dice cosa pensi direttamente, non ci si tira indietro per proteggere la propria reputazione — ci si confronta con lei per capire come mai rimanda quell'immagine. Prima si comprende, poi si agisce.",
  },
  {
    titolo: "L'accoglienza prima dello smistamento",
    quando: 'Un utente, cittadino o cliente è agitato, lamenta un disservizio, alza la voce o fa una richiesta che ritieni infondata.',
    logica: "Lo sportello della PA non smista problemi — li accoglie. Rimandare all'ufficio reclami o ignorare è sempre sbagliato. La risposta corretta è ascoltare, prendere nota, rassicurare.",
    esempio: "Un utente protesta per una faccenda che ritieni infondata. Non lo si indirizza all'ufficio reclami, non lo si ignora — ci si mostra comprensivi, si prendono nota delle lamentele e si rassicura che riceverà una risposta dall'ufficio competente. L'accoglienza è un valore, non una cortesia opzionale.",
  },
  {
    titolo: "L'equilibrio prima della reazione",
    quando: 'Sei sotto stress, hai dimenticato qualcosa, sei in ritardo, hai commesso un errore, sei sopraffatto dal carico di lavoro.',
    logica: "La PA valuta la capacità di gestire sé stessi con lucidità. Né negare il problema né cedere all'agitazione — riconoscere, prioritizzare, agire con calma.",
    esempio: "Nell'ultimo anno sei stato sottoposto a ritmi serrati e cominci a sentire effetti negativi sulla memoria. Non si ignora il problema, non ci si agita — si rallenta il ritmo, si organizzano le priorità, si trovano strategie concrete. L'autoregolazione è una competenza professionale.",
  },
  {
    titolo: "L'inclusione prima della comodità",
    quando: 'Ti viene assegnato un compito di affiancamento, tutoraggio o formazione verso qualcuno che ha difficoltà — anche difficoltà particolari.',
    logica: "La PA valorizza l'inclusione attiva. Chiedere di essere sostituiti o ridurre il supporto al minimo sono risposte sbagliate. Ci si impegna genuinamente, si cerca il modo.",
    esempio: "Sei nominato tutor di un apprendista con disturbo dello spettro autistico. Non si chiede di essere sostituiti, non si riduce l'impegno — ci si adopera per essere un buon tutor e supportare le difficoltà relazionali e comportamentali, chiedendo aiuto agli esperti se necessario. L'impegno viene prima del comfort.",
  },
  {
    titolo: "L'azione concreta prima dell'attesa",
    quando: "C'è un imprevisto, un'emergenza tecnica, una crisi operativa che blocca il lavoro.",
    logica: "Aspettare che qualcun altro risolva non è mai la risposta giusta. La PA valorizza chi trova una soluzione pratica nell'immediato, anche limitata, piuttosto che chi si paralizza.",
    esempio: "Un virus informatico rende inutilizzabile una presentazione urgente e importante. Non si aspettano gli esperti IT, non si convocano riunioni — si costruisce una nuova presentazione più limitata e si va avanti. La continuità operativa è una responsabilità personale.",
  },
];

export default function QuizLoop() {
  const { user, loading: authLoading } = useAuth();
  const { setOnGoHome } = useUI();
  const router = useRouter();

  const [phase, setPhase] = useState<Phase>('loading');
  const [modules, setModules] = useState<Module[]>([]);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [nextActivity, setNextActivity] = useState<NextActivity | null>(null);

  const [stepId, setStepId] = useState<string | null>(null);
  const [stepNumber, setStepNumber] = useState<number>(1);
  const [stepTema, setStepTema] = useState<string | null>(null);
  const [quizzes, setQuizzes] = useState<QuizItem[]>([]);
  const [contextIndex, setContextIndex] = useState(0);
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [answers, setAnswers] = useState<Answer[]>([]);

  const [recallId, setRecallId] = useState<string | null>(null);
  const [recallType, setRecallType] = useState<string | null>(null);
  const [dailyReviewId, setDailyReviewId] = useState<string | null>(null);

  const [adaptiveMode, setAdaptiveMode] = useState<string>('standard');
  const [resultMessage, setResultMessage] = useState<string>('');
  const [openAccordion, setOpenAccordion] = useState<string | null>(() =>
    typeof window !== 'undefined' ? (sessionStorage.getItem('openAccordion') ?? null) : null
  );

  // VF state
  const [vfOpen, setVfOpen] = useState(false);
  const [vfDetail, setVfDetail] = useState<number | null>(null);

  const ALL_DISC_KEYS = ['DA', 'DP', 'TD', 'CAD', 'PI', 'CP', 'CS', 'UE', 'OA', 'INF', 'ING', 'LG', 'QS'];
  const [disciplinesOrder, setDisciplinesOrder] = useState<string[]>(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem('disciplinesOrder');
      if (saved) {
        const parsed: string[] = JSON.parse(saved);
        return [...parsed, ...ALL_DISC_KEYS.filter(k => !parsed.includes(k))];
      }
    }
    return ALL_DISC_KEYS;
  });
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);

  const busyRef = useRef(false);

  const [typewriterText, setTypewriterText] = useState('');
  const [typewriterDone, setTypewriterDone] = useState(false);
  const [contextSummary, setContextSummary] = useState(false);
  const typewriterRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const contextBottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!authLoading) {
      if (user) {
        loadModules();
      } else {
        router.push('/login');
      }
    }
  }, [authLoading, user]);

  useEffect(() => {
    setOnGoHome(() => handleBackToModules);
    return () => setOnGoHome(null);
  }, []);

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState === 'visible' && phase === 'loading') {
        loadModules();
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'context' || contextSummary) return;
    const fullText = quizzes[contextIndex]?.versione_affermativa || '';
    setTypewriterText('');
    setTypewriterDone(false);
    if (typewriterRef.current) clearInterval(typewriterRef.current);
    let i = 0;
    const id = setInterval(() => {
      i++;
      setTypewriterText(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(id);
        setTypewriterDone(true);
      }
    }, 22);
    typewriterRef.current = id;
    return () => clearInterval(id);
  }, [contextIndex, phase, contextSummary]);

  useEffect(() => {
    if ((typewriterDone || contextSummary) && phase === 'context') {
      contextBottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [typewriterDone, contextSummary, phase]);

  async function loadModules() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch(`/api/archivista/modules`, { signal: controller.signal });
      clearTimeout(timeoutId);
      const data = await res.json();
      setModules(data.modules || []);

      const inProgress = data.modules?.find(
        (m: Module) => m.status === 'in_progress' && m.current_step < m.step_count
      );
      if (inProgress) {
        handleSelectModule(inProgress.nome);
      } else {
        setPhase('select_module');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Errore caricamento moduli:', error);
      setPhase('select_module');
    }
  }

  async function handleSelectModule(moduloNome: string) {
    if (busyRef.current) return;
    busyRef.current = true;
    setSelectedModule(moduloNome);
    setPhase('loading');

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    try {
      const res = await fetch(
        `/api/archivista/next?modulo_nome=${encodeURIComponent(moduloNome)}`,
        { signal: controller.signal }
      );
      clearTimeout(timeoutId);
      const data = await res.json();
      setNextActivity(data.next_activity);

      if (data.next_activity?.type === 'new_step') {
        await startStep(moduloNome);
      } else if (
        data.next_activity?.type === 'recall_r1' ||
        data.next_activity?.type === 'recall_r2'
      ) {
        await startRecall(data.next_activity.recall.id);
      } else if (data.next_activity?.type === 'daily_review') {
        await startDailyReview(data.next_activity.daily_review.id);
      } else if (data.next_activity?.type === 'module_complete') {
        setPhase('module_complete');
      } else {
        setPhase('select_module');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Errore:', error);
      setPhase('select_module');
    } finally {
      busyRef.current = false;
    }
  }

  async function startStep(moduloNome: string, retrain?: boolean) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch('/api/archivista/step/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modulo_nome: moduloNome, retrain: retrain ?? false }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const data = await res.json();

      if (data.step && data.quizzes && data.quizzes.length > 0) {
        setStepId(data.step.id);
        setStepNumber(data.step.step_number || 1);
        setStepTema(data.step.tema || null);
        setQuizzes(data.quizzes);
        setContextIndex(0);
        setContextSummary(false);
        setQuizIndex(0);
        setAnswers([]);
        setRecallId(null);
        setRecallType(null);
        setDailyReviewId(null);
        setPhase(moduloNome.startsWith('QS') ? 'quiz' : 'context');
      } else {
        setPhase('module_complete');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Errore avvio step:', error);
      setPhase('select_module');
    }
  }

  async function startRecall(recall_id: string) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const res = await fetch('/api/archivista/recall/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recall_id }),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      const data = await res.json();

      if (data.quizzes && data.quizzes.length > 0) {
        setRecallId(recall_id);
        setRecallType(data.recall_type);
        setQuizzes(data.quizzes);
        setQuizIndex(0);
        setAnswers([]);
        setSelectedOption(null);
        setShowResult(false);
        setStepId(null);
        setDailyReviewId(null);
        setPhase('recall');
      } else {
        setPhase('module_complete');
      }
    } catch (error) {
      clearTimeout(timeoutId);
      console.error('Errore avvio recall:', error);
      setPhase('select_module');
    }
  }

  async function startDailyReview(daily_review_id: string) {
    try {
      const res = await fetch('/api/archivista/recall/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ daily_review_id }),
      });
      const data = await res.json();

      if (data.quizzes && data.quizzes.length > 0) {
        setDailyReviewId(daily_review_id);
        setRecallType('daily');
        setQuizzes(data.quizzes);
        setQuizIndex(0);
        setAnswers([]);
        setSelectedOption(null);
        setShowResult(false);
        setStepId(null);
        setRecallId(null);
        setPhase('recall');
      } else {
        setPhase('select_module');
      }
    } catch (error) {
      console.error('Errore avvio daily review:', error);
      setPhase('select_module');
    }
  }

  function handleContextNext() {
    if (contextIndex + 1 >= quizzes.length) {
      setPhase('quiz');
      setQuizIndex(0);
      setSelectedOption(null);
      setShowResult(false);
    } else {
      setContextIndex(prev => prev + 1);
    }
  }

  function handleSelectOption(index: number) {
    if (showResult) return;

    const quiz = quizzes[quizIndex];
    const correctIdx = ['A', 'B', 'C', 'D'].indexOf(quiz.correct_answer_letter);
    const correct = index === correctIdx;

    setSelectedOption(index);
    setIsCorrect(correct);
    setShowResult(true);

    const newAnswers = [...answers, { quiz_id: quiz.id, correct }];
    setAnswers(newAnswers);

    setTimeout(() => {
      handleNext(newAnswers);
    }, 2000);
  }

  function handleNext(currentAnswers?: Answer[]) {
    if (quizIndex + 1 >= quizzes.length) {
      if (phase === 'quiz' && stepId) {
        completeStep(currentAnswers);
      } else if (phase === 'recall') {
        completeRecall(currentAnswers);
      } else {
        completeStep(currentAnswers);
      }
    } else {
      setQuizIndex(prev => prev + 1);
      setSelectedOption(null);
      setShowResult(false);
      setIsCorrect(false);
    }
  }

  async function completeStep(currentAnswers?: Answer[]) {
    if (!stepId || !selectedModule) return;

    const finalAnswers = currentAnswers ?? answers;
    setPhase('loading');

    try {
      const res = await fetch('/api/archivista/step/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          step_id: stepId,
          answers: finalAnswers,
          user_id: user?.id,
          modulo_nome: selectedModule,
          step_number: stepNumber,
        }),
      });
      const data = await res.json();

      setAdaptiveMode(data.result?.adaptive_mode || 'standard');
      setResultMessage(data.message || '');
      setPhase('results');
    } catch (error) {
      console.error('Errore completamento:', error);
      setPhase('results');
    }
  }

  async function completeRecall(currentAnswers?: Answer[]) {
    if (!recallId && !dailyReviewId) return;

    const finalAnswers = currentAnswers ?? answers;
    setPhase('loading');

    try {
      const res = await fetch('/api/archivista/recall/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          recall_id: recallId,
          daily_review_id: dailyReviewId,
          answers: finalAnswers,
          user_id: user?.id,
        }),
      });
      const data = await res.json();

      setResultMessage(data.message || 'Ripasso completato!');
      setPhase('results');
    } catch (error) {
      console.error('Errore completamento recall:', error);
      setPhase('results');
    }
  }

  function handleBackToModules() {
    busyRef.current = false;
    setPhase('select_module');
    setSelectedModule(null);
    loadModules();
  }

  const correctCount = answers.filter(a => a.correct).length;
  const percentage = answers.length > 0 ? Math.round((correctCount / answers.length) * 100) : 0;
  const errorCount = answers.filter(a => !a.correct).length;

  // ============ RENDER ============

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-44px)]">
        <div className="text-slate-400 text-sm">Caricamento...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-44px)]">
        <div className="text-red-400 text-sm">Autenticazione richiesta</div>
      </div>
    );
  }

  if (phase === 'loading') {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-44px)]">
        <div className="text-slate-400 text-sm">Caricamento...</div>
      </div>
    );
  }

  // Selezione modulo — accordion per disciplina
  if (phase === 'select_module') {
    const DISCIPLINES = [
      { key: 'DA',  name: 'Diritto Amministrativo',          test: (n: string) => n.startsWith('M') },
      { key: 'DP',  name: 'Diritto Penale',                  test: (n: string) => n.startsWith('DP') },
      { key: 'TD',  name: 'Trattamento Dati',                test: (n: string) => n.startsWith('TD') },
      { key: 'CAD', name: 'Codice Amministrazione Digitale', test: (n: string) => n.startsWith('CAD') },
      { key: 'PI',  name: 'Pubblico Impiego',                test: (n: string) => n.startsWith('PI') },
      { key: 'CP',  name: 'Contratti Pubblici',              test: (n: string) => n.startsWith('CP') },
      { key: 'CS',  name: 'Contabilità di Stato',            test: (n: string) => n.startsWith('CS') },
      { key: 'UE',  name: 'Diritto Unione Europea',          test: (n: string) => n.startsWith('UE') },
      { key: 'OA',  name: 'Ordinamento Amministrazioni',     test: (n: string) => n.startsWith('OA') },
      { key: 'INF', name: 'Informatica',                     test: (n: string) => n.startsWith('INF') },
      { key: 'ING', name: 'Lingua Inglese',                  test: (n: string) => n.startsWith('ING') },
      { key: 'LG',  name: 'Logica',                          test: (n: string) => n.startsWith('LG') },
      { key: 'QS',  name: 'Quiz Situazionali',               test: (n: string) => n.startsWith('QS') },
    ];

    const moduleCard = (m: Module) => (
      <button
        key={m.nome}
        onClick={() => handleSelectModule(m.nome)}
        className="w-full p-4 bg-white border-2 border-slate-200 rounded-xl text-left hover:border-blue-400 transition"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span>
                {m.status === 'completed' ? '✅' :
                 m.status === 'in_progress' ? '📖' : '📚'}
              </span>
              <span className="font-medium text-slate-800">{m.nome}</span>
              <span className="text-xs text-slate-400">· {m.quiz_count} quiz</span>
            </div>
            <div className="text-xs text-slate-500 mb-1">
              {m.is_retraining
                ? `🔄 Retraining in corso${m.last_score_percentage != null ? ` · Precedente: ${m.last_score_percentage}%` : ''}`
                : m.has_pending_recall
                ? `🔄 Ripasso disponibile${m.last_score_percentage != null ? ` · ${m.last_score_percentage}%` : ''}`
                : m.status === 'completed'
                ? `✅ Completato · ${m.last_score_percentage != null ? `${m.last_score_percentage}%` : '—'}`
                : m.status === 'in_progress'
                ? '📖 Training in corso'
                : '📚 Non iniziato'
              }
            </div>
            {m.last_accessed_at && (
              <div className="text-xs text-slate-400 mt-1">
                Ultimo accesso: {new Date(m.last_accessed_at).toLocaleDateString('it-IT', {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
                })}
              </div>
            )}
          </div>
          <span className="text-slate-400 ml-2">→</span>
        </div>
      </button>
    );

    return (
      <div className="flex flex-col h-[calc(100vh-44px)] overflow-y-auto">
        <div className="p-4 max-w-2xl mx-auto w-full space-y-3">
          {[...DISCIPLINES].sort((a, b) => disciplinesOrder.indexOf(a.key) - disciplinesOrder.indexOf(b.key)).map(({ key, name, test }) => {
            const moduleNum = (nome: string) => parseInt(nome.replace(/^[A-Z]+/, ''), 10) || 0;
            const disc = modules.filter(m => test(m.nome)).sort((a, b) => moduleNum(a.nome) - moduleNum(b.nome));
            if (disc.length === 0) return null;

            const totalQuiz    = disc.reduce((s, m) => s + m.quiz_count, 0);
            const modulesDone  = disc.filter(m => m.status === 'completed').length;
            const correctTotal = disc.reduce((s, m) =>
              s + (m.last_score_percentage != null
                ? Math.round(m.last_score_percentage / 100 * m.quiz_count)
                : 0), 0);
            const correctPct   = totalQuiz > 0 ? Math.round(correctTotal / totalQuiz * 100) : 0;
            const hoursStudied = disc
              .filter(m => m.status === 'completed')
              .reduce((s, m) => s + m.quiz_count * 1.5 / 60, 0);
            const remaining97  = Math.max(0, Math.round(totalQuiz * 0.97) - correctTotal);
            const hoursTo97    = remaining97 * 2.5 / 60;

            const isOpen = openAccordion === key;

            return (
              <div
                key={key}
                draggable
                onDragStart={e => { e.dataTransfer.effectAllowed = 'move'; setDraggingKey(key); }}
                onDragEnd={() => { setDraggingKey(null); setDragOverKey(null); }}
                onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (dragOverKey !== key) setDragOverKey(key); }}
                onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverKey(null); }}
                onDrop={e => {
                  e.preventDefault();
                  setDragOverKey(null);
                  setDraggingKey(null);
                  if (!draggingKey || draggingKey === key) return;
                  const order = [...disciplinesOrder];
                  const fromIdx = order.indexOf(draggingKey);
                  const toIdx = order.indexOf(key);
                  order.splice(fromIdx, 1);
                  order.splice(toIdx, 0, draggingKey);
                  setDisciplinesOrder(order);
                  sessionStorage.setItem('disciplinesOrder', JSON.stringify(order));
                }}
                className={[
                  'border-2 rounded-xl overflow-hidden transition-all duration-150',
                  draggingKey === key
                    ? 'opacity-40 scale-[0.98] shadow-none border-slate-300'
                    : dragOverKey === key && draggingKey !== key
                    ? 'border-blue-400 shadow-md bg-blue-50'
                    : 'border-slate-200',
                ].join(' ')}
              >
                {/* Header accordion */}
                <div className="flex items-stretch bg-white hover:bg-slate-50 transition">
                  <div
                    className="flex flex-col items-center justify-center px-3 gap-[3px] cursor-grab active:cursor-grabbing select-none text-slate-400 hover:text-slate-600 transition-colors"
                    title="Trascina per riordinare"
                  >
                    <span className="block w-4 h-[2px] rounded bg-current" />
                    <span className="block w-4 h-[2px] rounded bg-current" />
                    <span className="block w-4 h-[2px] rounded bg-current" />
                  </div>
                  <button
                    className="flex-1 py-4 pr-4 text-left"
                    onClick={() => {
                      const next = isOpen ? null : key;
                      setOpenAccordion(next);
                      if (next) sessionStorage.setItem('openAccordion', next);
                      else sessionStorage.removeItem('openAccordion');
                      if (!next) { setVfOpen(false); setVfDetail(null); }
                    }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-800 text-base">
                        {name}
                        {!isOpen && <span className="text-slate-400 font-normal text-sm ml-2">· {totalQuiz} quiz</span>}
                      </span>
                      <span className="text-slate-400 text-xs">{isOpen ? '▲' : '▼'}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                      <span>📚 {modulesDone}/{disc.length}</span>
                      <span>✅ {correctPct}%</span>
                      <span>⏱ {Math.round(hoursStudied * 10) / 10}h</span>
                      <span>🎯 {hoursTo97 > 0 ? `−${Math.round(hoursTo97 * 10) / 10}h` : '97%✓'}</span>
                    </div>
                  </button>
                </div>

                {/* Contenuto espanso */}
                {isOpen && (
                  <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-2">

                    {/* VF — solo per QS */}
                    {key === 'QS' && (
                      <div className="w-full rounded-xl border-2 border-amber-200 overflow-hidden bg-white">

                        {/* Bottone principale VF */}
                        <button
                          className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-amber-50 transition"
                          onClick={() => {
                            setVfOpen(o => !o);
                            setVfDetail(null);
                          }}
                        >
                          <span className="flex items-center gap-2">
                            <span>⭐</span>
                            <span className="font-semibold text-amber-900 text-sm">VF — Valori Firma della PA</span>
                          </span>
                          <span className="text-amber-400 text-xs">{vfOpen ? '▲' : '▼'}</span>
                        </button>

                        {/* Lista 10 valori */}
                        {vfOpen && vfDetail === null && (
                          <div className="border-t border-amber-100 divide-y divide-amber-50">
                            {VF_DATA.map((vf, i) => (
                              <button
                                key={i}
                                className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-amber-50 transition"
                                onClick={() => setVfDetail(i)}
                              >
                                <span className="text-xs text-amber-900">
                                  <span className="font-semibold">{i + 1}.</span> {vf.titolo}
                                </span>
                                <span className="text-amber-400 text-xs ml-2 flex-shrink-0">→</span>
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Scheda dettaglio singolo valore */}
                        {vfOpen && vfDetail !== null && (
                          <div className="border-t border-amber-100">
                            {/* ← torna alla lista */}
                            <button
                              className="w-full px-4 py-3 text-left flex items-center gap-2 hover:bg-amber-50 transition border-b border-amber-100"
                              onClick={() => setVfDetail(null)}
                            >
                              <span className="text-amber-400 text-xs">←</span>
                              <span className="text-xs font-semibold text-amber-900">
                                {vfDetail + 1}. {VF_DATA[vfDetail].titolo}
                              </span>
                            </button>
                            {/* Righe: tag sx + testo dx */}
                            <div className="divide-y divide-amber-50">
                              {[
                                { tag: 'Quando', testo: VF_DATA[vfDetail].quando },
                                { tag: 'La logica', testo: VF_DATA[vfDetail].logica },
                                { tag: 'Esempio', testo: VF_DATA[vfDetail].esempio },
                              ].map(({ tag, testo }) => (
                                <div key={tag} className="flex gap-3 px-4 py-3">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400 w-16 flex-shrink-0 pt-0.5">
                                    {tag}
                                  </span>
                                  <span className="text-xs text-slate-700 leading-relaxed">{testo}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Moduli quiz standard */}
                    {disc.map(moduleCard)}
                  </div>
                )}
              </div>
            );
          })}

          {modules.length === 0 && (
            <div className="text-center text-slate-400 py-8">Nessun modulo disponibile</div>
          )}
        </div>
      </div>
    );
  }

  // Fase MODULE COMPLETE
  if (phase === 'module_complete') {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-44px)] px-4">
        <div className="w-full max-w-md text-center">
          <div className="text-6xl mb-4">✅</div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            {selectedModule}
          </h2>
          <p className="text-slate-500 mb-8">
            Hai completato il training di questo modulo.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                if (selectedModule) startStep(selectedModule, true);
              }}
              className="w-full p-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
            >
              🔄 Retraining
            </button>
            <button
              onClick={handleBackToModules}
              className="w-full p-3 text-slate-600 hover:text-slate-800 transition"
            >
              ← Torna ai moduli
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Fase CONTEXT
  if (phase === 'context') {
    const seenQuizzes = contextSummary ? quizzes : quizzes.slice(0, contextIndex);
    const progressCount = contextSummary ? quizzes.length : contextIndex + 1;
    const progressPct = (progressCount / quizzes.length) * 100;
    const moduleLabel = [selectedModule, stepTema].filter(Boolean).join(' · ').toUpperCase();

    return (
      <div className="flex flex-col h-[calc(100vh-44px)]">
        <div className="flex-none px-4 pt-3 pb-3 max-w-2xl mx-auto w-full">
          <div className="text-center pb-1">
            <span className="text-xs font-bold tracking-[0.25em] text-slate-400 uppercase">
              Esposizione
            </span>
          </div>
          {moduleLabel && (
            <div className="text-center pb-2">
              <span className="text-[10px] font-semibold tracking-widest text-slate-500">
                {moduleLabel}
              </span>
            </div>
          )}
          <div className="flex justify-end text-[10px] text-slate-400 mb-1">
            {progressCount}/{quizzes.length}
          </div>
          <div className="h-0.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-orange-400 transition-all duration-300"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 max-w-2xl mx-auto w-full">
          {seenQuizzes.map((q) => (
            <div key={q.id} className="mb-3">
              <p className="text-sm text-slate-400 leading-snug">{q.versione_affermativa}</p>
            </div>
          ))}

          {!contextSummary && quizzes[contextIndex] && (
            <div className="mt-4 mb-6">
              <p className="text-lg text-slate-800 leading-relaxed">
                {typewriterText}
                {!typewriterDone && (
                  <span className="inline-block w-0.5 h-5 bg-slate-700 align-middle ml-0.5 animate-pulse" />
                )}
              </p>
            </div>
          )}

          {(contextSummary || typewriterDone) && (
            <div className="flex justify-center mt-2 mb-8">
              {contextSummary ? (
                <button
                  onClick={handleContextNext}
                  className="px-8 py-2.5 bg-orange-500 text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition"
                >
                  Quiz →
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (contextIndex + 1 >= quizzes.length) {
                      setContextSummary(true);
                    } else {
                      setContextIndex(prev => prev + 1);
                    }
                  }}
                  className="px-8 py-2.5 bg-orange-500 text-white rounded-xl font-semibold text-sm hover:bg-orange-600 transition"
                >
                  Prossima →
                </button>
              )}
            </div>
          )}

          <div ref={contextBottomRef} />
        </div>
      </div>
    );
  }

  // Fase QUIZ
  if (phase === 'quiz' && quizzes[quizIndex]) {
    const quiz = quizzes[quizIndex];
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-44px)] px-4">
        <div className="w-full max-w-2xl">
          {(selectedModule || stepTema) && (
            <div className="text-center mb-4 pb-3 border-b border-slate-200">
              <div className="text-xs text-slate-500 uppercase tracking-wider">
                {selectedModule}{stepTema && ` · ${stepTema}`}
              </div>
            </div>
          )}

          <div className="mb-4">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>❓ Verifica</span>
              <span>{correctCount}/{answers.length} · {quizIndex + 1}/{quizzes.length}</span>
            </div>
            <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all"
                style={{ width: `${((quizIndex + 1) / quizzes.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="text-center mb-8">
            <p className="text-xl text-slate-800 leading-relaxed">
              {quiz.versione_interrogativa}
            </p>
          </div>

          <div className="space-y-3">
            {(quiz.options_quantized || []).map((option, index) => {
              if (!option) return null;

              const letter = ['A', 'B', 'C', 'D'][index];
              const isSelected = selectedOption === index;
              const isCorrectOption = quiz.correct_answer_letter === letter;
              const isWrong = isSelected && showResult && !isCorrect;

              let bgColor = 'bg-white hover:bg-slate-50';
              let borderColor = 'border-slate-200';
              let textColor = 'text-slate-800';

              if (showResult) {
                if (isCorrectOption) {
                  bgColor = 'bg-green-50';
                  borderColor = 'border-green-500';
                  textColor = 'text-green-800';
                } else if (isWrong) {
                  bgColor = 'bg-red-50';
                  borderColor = 'border-red-500';
                  textColor = 'text-red-800';
                }
              } else if (isSelected) {
                borderColor = 'border-blue-500';
                bgColor = 'bg-blue-50';
              }

              return (
                <button
                  key={index}
                  onClick={() => handleSelectOption(index)}
                  disabled={showResult}
                  className={`w-full p-4 border-2 rounded-xl text-left transition ${bgColor} ${borderColor} ${textColor} ${showResult ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="font-semibold min-w-[24px]">{letter})</div>
                    <div className="flex-1">{option}</div>
                    {showResult && isCorrectOption && (
                      <div className="text-green-600 text-xl">✓</div>
                    )}
                    {isWrong && (
                      <div className="text-red-600 text-xl">✗</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {showResult && (
            <div className="mt-6 text-center text-sm text-slate-500">
              Avanzamento automatico...
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fase RECALL
  if (phase === 'recall' && quizzes[quizIndex]) {
    const quiz = quizzes[quizIndex];

    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-44px)] px-4">
        <div className="w-full max-w-2xl">
          <div className="text-center mb-4 pb-3 border-b border-amber-200">
            <div className="text-xs text-amber-600 uppercase tracking-wider mb-1">
              🔄 Ripasso errori
            </div>
            {selectedModule && (
              <div className="text-xs text-slate-400">{selectedModule}</div>
            )}
          </div>

          <div className="mb-4">
            <div className="flex justify-between text-xs text-slate-400 mb-1">
              <span>Ripasso</span>
              <span>{correctCount}/{answers.length} · {quizIndex + 1}/{quizzes.length}</span>
            </div>
            <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-amber-500 transition-all"
                style={{ width: `${((quizIndex + 1) / quizzes.length) * 100}%` }}
              />
            </div>
          </div>

          <div className="text-center mb-8">
            <p className="text-xl text-slate-800 leading-relaxed">
              {quiz.versione_interrogativa}
            </p>
          </div>

          <div className="space-y-3">
            {(quiz.options_quantized || []).map((option, index) => {
              if (!option) return null;

              const letter = ['A', 'B', 'C', 'D'][index];
              const isSelected = selectedOption === index;
              const isCorrectOption = quiz.correct_answer_letter === letter;
              const isWrong = isSelected && showResult && !isCorrect;

              let bgColor = 'bg-white hover:bg-slate-50';
              let borderColor = 'border-slate-200';
              let textColor = 'text-slate-800';

              if (showResult) {
                if (isCorrectOption) {
                  bgColor = 'bg-green-50';
                  borderColor = 'border-green-500';
                  textColor = 'text-green-800';
                } else if (isWrong) {
                  bgColor = 'bg-red-50';
                  borderColor = 'border-red-500';
                  textColor = 'text-red-800';
                }
              } else if (isSelected) {
                borderColor = 'border-amber-500';
                bgColor = 'bg-amber-50';
              }

              return (
                <button
                  key={index}
                  onClick={() => handleSelectOption(index)}
                  disabled={showResult}
                  className={`w-full p-4 border-2 rounded-xl text-left transition ${bgColor} ${borderColor} ${textColor} ${showResult ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="font-semibold min-w-[24px]">{letter})</div>
                    <div className="flex-1">{option}</div>
                    {showResult && isCorrectOption && (
                      <div className="text-green-600 text-xl">✓</div>
                    )}
                    {isWrong && (
                      <div className="text-red-600 text-xl">✗</div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>

          {showResult && (
            <div className="mt-6 text-center text-sm text-slate-500">
              Avanzamento automatico...
            </div>
          )}
        </div>
      </div>
    );
  }

  // Fase RESULTS
  if (phase === 'results') {
    const isRecallResult = recallId || dailyReviewId;
    const titleText = isRecallResult ? 'Ripasso completato!' : 'Step completato!';

    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-44px)] px-4">
        <div className="w-full max-w-md text-center">
          <div className="text-6xl mb-4">
            {percentage >= 85 ? '🎉' : percentage >= 60 ? '👍' : '💪'}
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">{titleText}</h2>
          <div className="text-5xl font-bold text-blue-600 mb-2">{percentage}%</div>
          <div className="text-slate-500 mb-6">
            {correctCount} su {answers.length} corrette
          </div>

          {!isRecallResult && adaptiveMode && (
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 ${
              adaptiveMode === 'express' ? 'bg-green-100 text-green-700' :
              adaptiveMode === 'intensive' ? 'bg-amber-100 text-amber-700' :
              'bg-blue-100 text-blue-700'
            }`}>
              <span>{adaptiveMode === 'express' ? '🚀' : adaptiveMode === 'intensive' ? '📚' : '⚖️'}</span>
              <span className="font-medium">
                Modalità {adaptiveMode === 'express' ? 'Express' : adaptiveMode === 'intensive' ? 'Intensiva' : 'Standard'}
              </span>
            </div>
          )}

          {resultMessage && (
            <p className="text-slate-600 mb-6">{resultMessage}</p>
          )}

          {!isRecallResult && errorCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left text-sm text-amber-800">
              <strong>🔄 {errorCount} {errorCount === 1 ? 'errore' : 'errori'} da ripassare</strong>
              <p className="mt-1 text-amber-700">Il ripasso partirà subito.</p>
            </div>
          )}

          {!isRecallResult && errorCount === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-sm text-green-800">
              <strong>✅ Perfetto!</strong> Nessun errore in questo step.
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={() => {
                if (selectedModule) {
                  handleSelectModule(selectedModule);
                }
              }}
              className="w-full p-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition"
            >
              {isRecallResult ? 'Continua →' : errorCount > 0 ? 'Inizia ripasso →' : 'Continua →'}
            </button>
            <button
              onClick={handleBackToModules}
              className="w-full p-3 text-slate-600 hover:text-slate-800 transition"
            >
              ← Cambia modulo
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center h-[calc(100vh-44px)]">
      <div className="text-slate-400 text-sm">Stato sconosciuto</div>
    </div>
  );
}